import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";

import { Icon } from "../components/Icon";
import { renderKbMarkdown } from "../components/kbMarkdown";
import {
  ChipInput,
  Field,
  Pill,
  Section,
  ToastProvider,
  errText,
  fmtDateTime,
  useToast,
} from "../admin/ui";
import {
  api,
  type AdminCompanyRow,
  type KbAdminDetail,
  type KbVersionRow,
} from "../services/api";

/** Mirrors KB_BODY_MAX_LENGTH in backend/app/routers/kb.py. Counted in code
 *  points (Array.from), which is what Python's len() counts. */
const KB_BODY_MAX_LENGTH = 200_000;

const CATEGORIES = [
  "Getting started",
  "Account & sign-in",
  "Security",
  "Networking",
  "Hardware",
  "Microsoft 365",
  "Applications",
  "Email",
  "Other",
];

type ScopeValue = "global" | "multi_company" | "single_company";

/**
 * Article editor — /admin/kb/new and /admin/kb/:id/edit.
 *
 * Body on the left, audience on the right, one footer that says exactly what
 * each button does. Publishing is one step and snapshots a version, so the
 * rollback list sits under the audience rather than hiding behind a menu.
 */
export function AdminKBEdit() {
  return (
    <ToastProvider>
      <AdminKBEditInner />
    </ToastProvider>
  );
}

function AdminKBEditInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = !id;

  const { data, mutate, error: loadError } = useSWR<KbAdminDetail>(
    !isNew ? `/api/admin/kb/${id}` : null,
    () => api.adminKb.get(id as string),
  );
  const { data: versions, mutate: mutateVersions } = useSWR<KbVersionRow[]>(
    !isNew && id ? `/api/admin/kb/${id}/versions` : null,
    () => api.adminKb.versions(id as string),
  );
  const { data: companies } = useSWR<AdminCompanyRow[]>("/api/admin/companies", () =>
    api.admin.companies.list(),
  );

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [summary, setSummary] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [scope, setScope] = useState<ScopeValue>("global");
  const [audienceIds, setAudienceIds] = useState<string[]>([]);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState<"draft" | "publish" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const seeded = useRef(false);
  useEffect(() => {
    if (!data || seeded.current) return;
    seeded.current = true;
    setTitle(data.title);
    setCategory(data.category);
    setSummary(data.summary ?? "");
    setTags(data.tags ?? []);
    setBody(data.body);
    setScope(data.scope);
    setAudienceIds(
      data.scope === "single_company"
        ? data.company_id
          ? [data.company_id]
          : []
        : (data.audience_company_ids ?? []),
    );
  }, [data]);

  function payload() {
    const ids = audienceIds;
    const effectiveScope: ScopeValue =
      scope === "global" ? "global" : ids.length === 1 ? "single_company" : "multi_company";
    return {
      title: title.trim(),
      // Keep leading whitespace. trim() would turn a leading indented code
      // block into a paragraph. Blank bodies are rejected in validate().
      body,
      category: category.trim(),
      scope: effectiveScope,
      tags,
      summary: summary.trim() || null,
      company_id: effectiveScope === "single_company" ? (ids[0] ?? null) : null,
      audience_company_ids: effectiveScope === "multi_company" ? ids : null,
    };
  }

  function validate(): string | null {
    if (!title.trim()) return "Give the article a title.";
    if (!category.trim()) return "Pick a category.";
    // Zero-width characters render as nothing, so a body of only those is
    // blank too. The API applies the same rule and the same cap (#240).
    if (!body.replace(/[\u200b\u200c\u200d\u2060\ufeff]/g, "").trim()) {
      return "The article needs a body.";
    }
    const length = Array.from(body).length;
    if (length > KB_BODY_MAX_LENGTH) {
      return `The article body is ${length.toLocaleString()} characters. The limit is ${KB_BODY_MAX_LENGTH.toLocaleString()}, so shorten it or split it into two articles.`;
    }
    if (scope !== "global" && audienceIds.length === 0) {
      return "Pick at least one customer, or make the article global.";
    }
    return null;
  }

  async function saveDraft() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy("draft");
    setError(null);
    try {
      if (isNew) {
        const created = await api.adminKb.create(payload());
        toast("Draft saved");
        navigate(`/admin/kb/${created.id}/edit`, { replace: true });
      } else {
        const saved = await api.adminKb.update(id as string, payload());
        await mutate(saved, { revalidate: false });
        toast("Draft saved");
      }
    } catch (e) {
      setError(errText(e, "Save failed."));
    } finally {
      setBusy(null);
    }
  }

  async function publish() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (
      !window.confirm(
        "Publish this article? It becomes visible to its audience immediately and snapshots a version.",
      )
    ) {
      return;
    }
    setBusy("publish");
    setError(null);
    try {
      let articleId = id as string;
      if (isNew) {
        articleId = (await api.adminKb.create(payload())).id;
      } else {
        await api.adminKb.update(articleId, payload());
      }
      const published = await api.adminKb.publish(articleId);
      toast("Article published");
      if (isNew) {
        navigate(`/admin/kb/${published.id}/edit`, { replace: true });
      } else {
        await mutate(published, { revalidate: false });
        await mutateVersions();
      }
    } catch (e) {
      setError(errText(e, "Publish failed."));
    } finally {
      setBusy(null);
    }
  }

  async function rollback(version: KbVersionRow) {
    if (
      !window.confirm(
        `Roll back to version ${version.version_number} (“${version.title}”)? The current draft is replaced.`,
      )
    ) {
      return;
    }
    try {
      const rolled = await api.adminKb.rollback(id as string, version.id);
      await mutate(rolled, { revalidate: false });
      setTitle(rolled.title);
      setCategory(rolled.category);
      setSummary(rolled.summary ?? "");
      setTags(rolled.tags ?? []);
      setBody(rolled.body);
      setScope(rolled.scope);
      setAudienceIds(
        rolled.scope === "single_company"
          ? rolled.company_id
            ? [rolled.company_id]
            : []
          : (rolled.audience_company_ids ?? []),
      );
      toast(`Rolled back to version ${version.version_number}`);
    } catch (e) {
      setError(errText(e, "Rollback failed."));
    }
  }

  if (loadError) {
    return (
      <div className="card form-error">
        Failed to load the article: {String((loadError as Error).message ?? loadError)}
      </div>
    );
  }
  if (!isNew && !data) {
    return <div className="card">Loading article…</div>;
  }

  return (
    <div className="rf-wrap">
      <Link className="ad-back" to="/admin/kb">
        <Icon name="arrowLeft" /> Knowledge base
      </Link>

      <div className="ad-head">
        <div>
          <div className="ad-eyebrow">{isNew ? "New article" : "Edit article"}</div>
          <h1 className="rf-h1">{title.trim() || "Write an article"}</h1>
          {!isNew && data && (
            <p className="rf-sub">/{data.slug}</p>
          )}
        </div>
        <div className="grp">
          {!isNew && data ? (
            data.status === "published" ? (
              <Pill kind="ok">Published</Pill>
            ) : (
              <Pill kind="warn">Draft</Pill>
            )
          ) : (
            <Pill kind="off">Not saved yet</Pill>
          )}
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div className="ad-editor">
        <div className="ad-cbody">
          <Section title="Article">
            <div className="ad-grid">
              <Field
                label="Title"
                span
                note="Start with the outcome, not the tool: “Set up MFA on your Microsoft account”."
              >
                <input
                  value={title}
                  maxLength={300}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Field>
              <Field label="Category">
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {!CATEGORIES.includes(category) && category && (
                    <option value={category}>{category}</option>
                  )}
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tags" note="Press Enter after each one.">
                <ChipInput
                  values={tags}
                  placeholder="mfa, microsoft, sign-in"
                  onAdd={(v) => setTags((prev) => [...prev, v])}
                  onRemove={(v) => setTags((prev) => prev.filter((t) => t !== v))}
                />
              </Field>
              <Field label="Summary" span note="The blurb shown in article lists.">
                <textarea
                  rows={2}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
              </Field>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "18px 0 6px",
              }}
            >
              <span className="ad-eyebrow">Body — Markdown</span>
              <label className="checkbox-row" style={{ marginBottom: 0, fontSize: 12.5 }}>
                <input
                  type="checkbox"
                  checked={preview}
                  onChange={(e) => setPreview(e.target.checked)}
                />
                <span>Preview</span>
              </label>
            </div>
            {preview ? (
              <div
                className="kb-article-body"
                dangerouslySetInnerHTML={{
                  __html: renderKbMarkdown(body || "_(empty)_"),
                }}
              />
            ) : (
              <div className="ad-field">
                <textarea
                  rows={16}
                  value={body}
                  placeholder="Headings, numbered steps and screenshots render as a guided walkthrough."
                  aria-label="Article body"
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
            )}
          </Section>

          <div className="ad-wiz-foot" style={{ border: 0, margin: 0 }}>
            <span />
            <span className="grp">
              <Link className="btn btn-ghost" to="/admin/kb">
                Cancel
              </Link>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void saveDraft()}
                disabled={busy !== null}
              >
                {busy === "draft" ? "Saving…" : "Save draft"}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void publish()}
                disabled={busy !== null}
              >
                {busy === "publish" ? "Publishing…" : "Publish"}
              </button>
            </span>
          </div>
        </div>

        <div className="ad-cbody">
          <Section title="Audience">
            <div className="ad-sev" style={{ gridTemplateColumns: "1fr", gap: 6 }}>
              <label>
                <input
                  type="radio"
                  name="kb-scope"
                  checked={scope === "global"}
                  onChange={() => setScope("global")}
                />
                <span className="t">All customers</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="kb-scope"
                  checked={scope !== "global"}
                  onChange={() => setScope("multi_company")}
                />
                <span className="t">Specific customers</span>
              </label>
            </div>
            {scope !== "global" && (
              <div style={{ marginTop: 10 }}>
                {(companies ?? []).map((c) => (
                  <label key={c.id} className="ad-pick" style={{ padding: "7px 10px" }}>
                    <input
                      type="checkbox"
                      checked={audienceIds.includes(c.id)}
                      onChange={(e) =>
                        setAudienceIds((prev) =>
                          e.target.checked
                            ? [...prev, c.id]
                            : prev.filter((x) => x !== c.id),
                        )
                      }
                    />
                    <span className="bd">
                      <span className="t" style={{ fontSize: 13 }}>
                        {c.name}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Section>

          {!isNew && (
            <Section
              title="Version history"
              desc="Every publish snapshots the article. Rolling back replaces the current draft."
            >
              {!versions || versions.length === 0 ? (
                <div className="ad-note">
                  No published versions yet. Publish to create the first snapshot.
                </div>
              ) : (
                versions.map((v) => (
                  <div className="ad-kv" key={v.id}>
                    <span className="k">
                      v{v.version_number}
                      <br />
                      <span style={{ fontSize: 12, color: "var(--iq-text-faint)" }}>
                        {fmtDateTime(v.published_at)}
                      </span>
                    </span>
                    <span className="v">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void rollback(v)}
                      >
                        Roll back
                      </button>
                    </span>
                  </div>
                ))
              )}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
